import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { format } from "date-fns";
import { Route as RouteIcon, Plus, Loader2, RefreshCw } from "lucide-react";

import { 
  RouteFormDialog, 
  RouteCard, 
  initialRouteForm 
} from "./routes";

/**
 * My Shared Routes Page
 * 
 * Manages partner's shared delivery routes.
 */
export default function MySharedRoutes() {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showAddRoute, setShowAddRoute] = useState(false);
  const [showEditRoute, setShowEditRoute] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [routeForm, setRouteForm] = useState(initialRouteForm);

  const fetchRoutes = useCallback(async () => {
    try {
      const response = await api.get("/shared-routes/my");
      setRoutes(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to fetch routes:', error.message);
      }
      setRoutes([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchRoutes();
  }, [fetchRoutes]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchRoutes();
    toast.success("Routes refreshed");
  };

  const resetRouteForm = () => {
    setRouteForm(initialRouteForm);
  };

  const parseViaStops = (viaString) => {
    return viaString 
      ? viaString.split(",").map((s) => s.trim()).filter(Boolean) 
      : [];
  };

  const buildRoutePayload = (form) => ({
    origin: form.origin,
    destination: form.destination,
    via_stops: parseViaStops(form.via_stops),
    vehicle_type: form.vehicle_type,
    total_space: parseFloat(form.total_capacity),
    price_per_kg: parseFloat(form.price_per_kg),
    departure_date: format(form.departure_date, "yyyy-MM-dd"),
    departure_time: form.departure_time,
  });

  const handleCreateRoute = async (e) => {
    e.preventDefault();
    try {
      await api.post("/shared-routes", buildRoutePayload(routeForm));
      toast.success("Route created successfully!");
      setShowAddRoute(false);
      resetRouteForm();
      fetchRoutes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create route");
    }
  };

  const handleEditClick = (route) => {
    setEditingRoute(route);
    setRouteForm({
      origin: route.origin,
      destination: route.destination,
      via_stops: route.via?.join(", ") || "",
      vehicle_type: route.vehicle,
      total_capacity: route.total_space.toString(),
      price_per_kg: route.price_per_kg.toString(),
      departure_date: new Date(route.departure_date),
      departure_time: route.departure_time,
    });
    setShowEditRoute(true);
  };

  const handleUpdateRoute = async (e) => {
    e.preventDefault();
    if (!editingRoute) return;
    
    try {
      await api.put(`/shared-routes/${editingRoute.id}`, buildRoutePayload(routeForm));
      toast.success("Route updated successfully!");
      setShowEditRoute(false);
      setEditingRoute(null);
      resetRouteForm();
      fetchRoutes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update route");
    }
  };

  const handleCancelRoute = async (routeId) => {
    if (!window.confirm("Are you sure you want to cancel this route? This cannot be undone.")) {
      return;
    }
    
    setCancelling(routeId);
    try {
      await api.delete(`/shared-routes/${routeId}`);
      toast.success("Route cancelled successfully");
      fetchRoutes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel route");
    } finally {
      setCancelling(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-black tracking-tight">My Delivery Routes</h1>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
            className="rounded-none gap-2"
            data-testid="refresh-routes-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button 
            onClick={() => setShowAddRoute(true)} 
            className="rounded-none gap-2" 
            data-testid="add-route-btn"
          >
            <Plus className="w-4 h-4" />
            Add Route
          </Button>
        </div>
      </div>

      {/* Add Route Dialog */}
      <RouteFormDialog
        open={showAddRoute}
        onOpenChange={setShowAddRoute}
        title="Create Shared Route"
        form={routeForm}
        setForm={setRouteForm}
        onSubmit={handleCreateRoute}
        submitLabel="Create Route"
      />

      {/* Edit Route Dialog */}
      <RouteFormDialog
        open={showEditRoute}
        onOpenChange={(open) => {
          setShowEditRoute(open);
          if (!open) {
            setEditingRoute(null);
            resetRouteForm();
          }
        }}
        title="Edit Route"
        form={routeForm}
        setForm={setRouteForm}
        onSubmit={handleUpdateRoute}
        submitLabel="Update Route"
      />

      {/* Routes List */}
      {routes.length === 0 ? (
        <EmptyRouteState onAddRoute={() => setShowAddRoute(true)} />
      ) : (
        <div className="grid gap-4">
          {routes.map((route) => (
            <RouteCard 
              key={route.id}
              route={route}
              onEdit={handleEditClick}
              onCancel={handleCancelRoute}
              cancelling={cancelling}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyRouteState({ onAddRoute }) {
  return (
    <div className="text-center py-10 border border-border">
      <RouteIcon className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
      <p className="text-muted-foreground mb-4">No shared routes yet</p>
      <p className="text-sm text-muted-foreground mb-4">
        Create a route to let shippers share your delivery capacity
      </p>
      <Button onClick={onAddRoute} className="rounded-none gap-2">
        <Plus className="w-4 h-4" />
        Create Your First Route
      </Button>
    </div>
  );
}
